import React, { useState, useEffect } from 'react';
import './WaitlistManagement.css';

const WaitlistManagement = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // cezec-landing API URL (canlıda www.cezec.dev domain'i - 307 CORS yönlendirmesini önlemek için www.cezec.dev kullanılıyor)
  const API_URL = process.env.REACT_APP_CEZEC_API_URL || 'https://www.cezec.dev';

  const fetchEntries = async () => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      params.append('page', page);

      const response = await fetch(`${API_URL}/api/waitlist?${params.toString()}`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Bekleme listesi verileri alınamadı`);
      }
      const data = await response.json();

      if (data.success) {
        setEntries(data.data || []);
        setTotal(data.total || 0);
        setTotalPages(data.totalPages || 1);
      }
    } catch (error) {
      console.error('Fetch error:', error);
      alert('Bekleme listesi yüklenemedi: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, statusFilter]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchEntries();
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      const response = await fetch(`${API_URL}/api/waitlist/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        fetchEntries();
      } else {
        alert('Güncelleme başarısız');
      }
    } catch (error) {
      console.error('Update error:', error);
      alert('Güncelleme başarısız');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu kaydı silmek istediğinizden emin misiniz?')) return;

    try {
      const response = await fetch(`${API_URL}/api/waitlist/${id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        fetchEntries();
      } else {
        alert('Silme başarısız');
      }
    } catch (error) {
      console.error('Delete error:', error);
      alert('Silme başarısız');
    }
  };

  const handleExport = async () => {
    try {
      window.location.href = `${API_URL}/api/waitlist/export`;
    } catch (error) {
      console.error('Export error:', error);
      alert('Export başarısız');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { label: 'Bekliyor', className: 'status-pending' },
      notified: { label: 'Bildirildi', className: 'status-notified' },
      rejected: { label: 'Reddedildi', className: 'status-rejected' }
    };

    const badge = badges[status] || badges.pending;
    return <span className={`status-badge ${badge.className}`}>{badge.label}</span>;
  };

  return (
    <div className="waitlist-management">
      <div className="page-header">
        <h1>🎯 Bekleme Listesi Yönetimi</h1>
        <p>CeZec Akıllı Ders Dağıtım Paneli - Erken Erişim Başvuruları</p>
      </div>

      <div className="actions-bar">
        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="İsim, email veya okul ara..."
            className="search-input"
          />
          <button type="submit" className="btn btn-search">
            🔍 Ara
          </button>
        </form>

        <div className="filters">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="">Tüm Durumlar</option>
            <option value="pending">Bekliyor</option>
            <option value="notified">Bildirildi</option>
            <option value="rejected">Reddedildi</option>
          </select>

          <button onClick={handleExport} className="btn btn-export">
            📥 CSV İndir
          </button>
        </div>
      </div>

      <div className="stats-cards">
        <div className="stat-card">
          <div className="stat-value">{total}</div>
          <div className="stat-label">Toplam Başvuru</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">
            {entries.filter(e => e.status === 'pending').length}
          </div>
          <div className="stat-label">Bekleyen</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">
            {entries.filter(e => e.status === 'notified').length}
          </div>
          <div className="stat-label">Bildirildi</div>
        </div>
      </div>

      {loading ? (
        <div className="loading">Yükleniyor...</div>
      ) : (
        <>
          <div className="table-container">
            <table className="waitlist-table">
              <thead>
                <tr>
                  <th>Ad Soyad</th>
                  <th>E-posta</th>
                  <th>Telefon</th>
                  <th>Okul</th>
                  <th>Görev</th>
                  <th>Kayıt Tarihi</th>
                  <th>Durum</th>
                  <th>İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr key={entry._id}>
                    <td>
                      <strong>{entry.fullName}</strong>
                    </td>
                    <td>{entry.email}</td>
                    <td>{entry.phone || '-'}</td>
                    <td>{entry.schoolName || '-'}</td>
                    <td>{entry.position || '-'}</td>
                    <td>{new Date(entry.createdAt).toLocaleDateString('tr-TR')}</td>
                    <td>{getStatusBadge(entry.status)}</td>
                    <td>
                      <div className="action-buttons">
                        {entry.status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleStatusChange(entry._id, 'notified')}
                              className="btn-action btn-notify"
                              title="Bildirildi Olarak İşaretle"
                            >
                              ✓
                            </button>
                            <button
                              onClick={() => handleStatusChange(entry._id, 'rejected')}
                              className="btn-action btn-reject"
                              title="Reddet"
                            >
                              ✕
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => handleDelete(entry._id)}
                          className="btn-action btn-delete"
                          title="Sil"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="btn btn-page"
              >
                ← Önceki
              </button>
              <span className="page-info">
                Sayfa {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page === totalPages}
                className="btn btn-page"
              >
                Sonraki →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default WaitlistManagement;
